/* La fecha de RTO que se lee del TÍTULO, y no del CENT.
 *
 * ── DE DÓNDE SALE ────────────────────────────────────────────────────────
 *
 * Un vehículo 0 km no rinde RTO por un año: se vence directo al año de
 * comprado. El CENT no tiene nada que decir de él, así que el hub lo dibujaba
 * como `sin_rto` — exactamente igual que un camión al que le falta el papel.
 * Los que cargan esto tienen la fecha impresa en el título y no tenían dónde
 * escribirla.
 *
 * Y el hueco ES la flota nueva: 92 tractores y 66 furgones activos sin fila en
 * `rto`, de los cuales 89 y 53 son 2025/2026 y ninguno más viejo. 142 de 158.
 *
 * ── EL CENT PISA LO DECLARADO ────────────────────────────────────────────
 *
 * Lo decidió Igna, y la dirección del error es lo que lo cierra: si la fecha a
 * mano le ganara, se podría destrabar una RTO vencida escribiendo — o sea
 * sacar un camión del portón tipeando. Al revés, lo peor que pasa es que un
 * dato viejo del título quede tapado por el oficial, que es lo correcto.
 *
 * No es la regla al revés de «una declaración le gana a una inferencia»: el
 * CENT no es una inferencia nuestra, es el registro del organismo.
 *
 * ── PERO LO DECLARADO SE SIGUE VIENDO CUANDO PIERDE ──────────────────────
 *
 * `rto_estado()` devuelve `declarada` gane o no. Escondida, el que la tipeó
 * nunca se entera de que no se está usando, y una fecha mal cargada se queda
 * ahí para siempre esperando el día que el CENT deje de contestar. Misma regla
 * que la discrepancia del teléfono y que `duplicados`: se muestra, no se
 * resuelve sola.
 *
 * Es puro —entra la fila de `rto_estado`, sale qué se puede decir y hacer— así
 * que todo esto se prueba en una tabla.
 */

/* La fecha que está mandando salió del CENT. */
export const laManda = (e) => e?.fuente === "cent";

/* Hay una fecha escrita a mano, gane o no. */
export const hayDeclarada = (e) => !!e?.declarada;

/* ¿La declarada es la que se está usando? Es lo que decide si la celda de
 * «Vence» hay que marcarla como declarada o no. */
export const declaradaGana = (e) => e?.fuente === "declarada";

/* ── CUÁNDO SE OFRECE EL CAMPO ────────────────────────────────────────────
 *
 * Dos casos, y el segundo es el que se olvida:
 *
 *   · el CENT no tiene nada → hay un hueco, y para eso es esto;
 *   · YA HAY UNA DECLARADA, aunque el CENT haya aparecido después → tiene que
 *     poder corregirse o borrarse. Sin esto, una fecha mal tipeada quedaría
 *     visible y sin forma de tocarla, y volvería a mandar el día que el CENT
 *     deje de contestar esa patente.
 *
 * `sin_patente` NO entra: sin dominio la fila quedaría huérfana —nadie la vería
 * y nadie sabría que está—. La base también lo rechaza; acá el campo ni se
 * ofrece, porque un campo que se puede llenar y siempre falla es peor que uno
 * que no está.
 *
 * ── Y SIN `estado` TAMPOCO: «NO SE MIRÓ» NO ES «HAY UN HUECO» ────────────
 *
 * Es la regla de siempre y acá muerde de verdad. La solapa BUSCAR de `/rto`
 * arma sus filas con `buscarCamion()`, que sale del PADRÓN: trae interno,
 * patente y empresa, y ninguna columna de RTO. Sin este chequeo, con el lápiz
 * quedado prendido de la otra solapa esas filas ofrecerían el campo —y
 * guardarlo escribiría una fecha del título sin saber si el CENT ya tiene la
 * suya, que es justamente lo único que decide si sirve para algo—.
 *
 * Cubre además el instante en que la ficha todavía está pidiendo el estado.
 */
export function sePuedeDeclarar(e) {
  if (!e || !e.estado || e.estado === "sin_patente") return false;
  return !laManda(e) || hayDeclarada(e);
}

/* Cómo se dice de dónde salió la fecha que está mandando.
 *
 * Sin esto, «vence el 12/03» del CENT y «vence el 12/03» que alguien tipeó se
 * leen igual, y no pesan lo mismo: uno lo dice el organismo y el otro lo
 * escribió una persona mirando un papel. */
export function origen(e) {
  if (laManda(e)) return "CENT";
  if (declaradaGana(e)) return "Declarada a mano";
  return null;
}

/* La firma, para el `title`: quién la cargó y por qué. Sin nombre no se
 * inventa uno — se dice que no se sabe, que es distinto de no decir nada. */
export function firma(e) {
  if (!hayDeclarada(e)) return null;
  const partes = [];
  if (e.declarada_motivo) partes.push(e.declarada_motivo);
  partes.push(e.declarada_por ? `cargó ${e.declarada_por}` : "sin autor");
  return partes.join(" · ");
}

/* ── EL AVISO DE DISCREPANCIA ─────────────────────────────────────────────
 *
 * El CENT apareció y hay una declarada distinta. No se resuelve solo: se dice.
 * Igual fecha no es discrepancia — es que la persona acertó, y avisar ahí
 * llenaría la pantalla de desacuerdos falsos. Misma regla que `telefono.elegir`
 * con los números normalizados.
 */
export function discrepa(e) {
  return !!(laManda(e) && hayDeclarada(e) && e.declarada !== e.vence);
}

/* ── LA FECHA QUE SE OFRECE POR DEFECTO ───────────────────────────────────
 *
 * Un año desde hoy es lo que dice la regla del 0 km, y se ofrece PRELLENADA
 * porque es la respuesta correcta casi siempre. Pero se ofrece para
 * CORREGIRLA: el año corre desde que se compró y no desde que alguien abre
 * esta pantalla, así que el que carga tiene el título delante y pone la que
 * dice el papel. Calcularla y guardarla sin preguntar sería inventar una fecha
 * que después nadie va a poder auditar.
 */
export function sugerida(hoy = new Date()) {
  const d = new Date(hoy.getFullYear() + 1, hoy.getMonth(), hoy.getDate());
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/* El motivo por el que casi siempre se carga esto. Es una SUGERENCIA en un
 * campo de texto libre y no un desplegable: el día que haga falta otro motivo
 * —una unidad importada, un régimen especial— se escribe, en vez de tener que
 * elegir el que menos miente. */
export const MOTIVO_SUGERIDO = "0 km — fecha del título";

/* ── REFRESCAR UNA FILA DE LA TABLA SIN RECALCULAR NADA ACÁ ───────────────
 *
 * Después de guardar hay que actualizar la fila, y la tentación es recalcular
 * el estado en el navegador: son cuatro `if`. No se hace — `rto_estado()` es
 * el único lugar donde se decide en qué condición está una RTO, y una segunda
 * copia acá diría una cosa en la tabla y otra en la ficha del mismo camión el
 * día que se mueva el corte de 30 días.
 *
 * Se vuelve a pedir ESA unidad y se le pegan estos campos encima. Es una RPC
 * de una fila, contra volver a traer 658.
 *
 * Y se pega en la fila EN SU LUGAR en vez de reordenar la lista: se cargan
 * de a 142 seguidas, y una fila que salta de posición al guardarla hace perder
 * dónde se estaba.
 */
export const CAMPOS_DEL_ESTADO = [
  "estado",
  "vence",
  "dias",
  "resultado",
  "revisado",
  "fuente",
  "declarada",
];

export function soloCampos(e) {
  const out = {};
  for (const k of CAMPOS_DEL_ESTADO) out[k] = e ? (e[k] ?? null) : null;
  return out;
}
